// Barra de abertos (modelo Zendesk): tudo o que está em andamento vira uma
// aba — card deixado aberto, conversa atual ou com rascunho, formulário pela
// metade. Nada aqui tem backend: lê o que o app já guarda para "continuar de
// onde parou" (sessionStorage/localStorage).
//
// A ordem é a de chegada (estável, como abas): `sophia:openTabs:order`.

import { SECTOR_NAMES } from '../pages/board-completo/config';
import { clearPersisted, writePersisted } from '../hooks/usePersistedState';
import { discardDraft, listDrafts } from '../components/tabs/atendimento/conversationMemory';
import { announceOpenTabsChange, forgetOpenCard, listOpenCards } from './conversationBridge';

export type OpenTabKind = 'card' | 'conversation' | 'form';

export interface OpenTab {
  id: string;
  kind: OpenTabKind;
  label: string;
  /** Contexto curto (setor, board) — opcional. */
  sub?: string;
  /** Para onde a aba leva (a página reencontra o estado guardado). */
  path: string;
  /** Algo digitado e não enviado — fechar pede confirmação. */
  hasDraft: boolean;
  /** A aba é a tela de agora? */
  isActive: (pathname: string) => boolean;
  /** Fecha a aba (e descarta o rascunho, se houver). */
  close: () => void;
  /** Card aberto na tela agora: não fecha pela barra (fecha-se o card). */
  closeDisabled?: boolean;
}

const CHAT_PATH = '/atendimento/conversas';
const ORDER_KEY = 'sophia:openTabs:order';
const CURRENT_CONVERSATION_KEY = 'sophia:openTabs:currentConversation';

// ── Conversa atual ───────────────────────────────────────────────────────

interface CurrentConversation { phone: string; name: string }

export function setCurrentConversation(conv: CurrentConversation | null): void {
  try {
    const prev = sessionStorage.getItem(CURRENT_CONVERSATION_KEY);
    const next = conv ? JSON.stringify(conv) : null;
    if (prev === next) return;
    if (next) sessionStorage.setItem(CURRENT_CONVERSATION_KEY, next);
    else sessionStorage.removeItem(CURRENT_CONVERSATION_KEY);
  } catch { /* ignora */ }
  announceOpenTabsChange();
}

function getCurrentConversation(): CurrentConversation | null {
  try {
    const raw = sessionStorage.getItem(CURRENT_CONVERSATION_KEY);
    return raw ? (JSON.parse(raw) as CurrentConversation) : null;
  } catch {
    return null;
  }
}

// ── Rótulos ──────────────────────────────────────────────────────────────

function pageLabel(pathname: string): string {
  const sector = pathname.match(/^\/board\/([^/]+)/)?.[1];
  if (sector) return SECTOR_NAMES[sector] ?? readTitle(`boardCompleto:${sector}:title`) ?? 'Board';
  if (pathname.startsWith('/atendimento/captacao')) return 'Captação';
  if (pathname.startsWith('/atendimento/matriculas')) return 'Matrículas';
  if (pathname.startsWith('/solicitacoes')) return 'Solicitações';
  if (pathname.startsWith('/boards/triagem')) return 'Triagem';
  return '';
}

function readFlag(key: string): boolean {
  try { return sessionStorage.getItem(`sophia:${key}`) === 'true'; } catch { return false; }
}

function readTitle(key: string): string | null {
  try {
    const raw = sessionStorage.getItem(`sophia:${key}`);
    return raw ? (JSON.parse(raw) as string) : null;
  } catch { return null; }
}

// ── Coleta ───────────────────────────────────────────────────────────────

function collect(): OpenTab[] {
  const tabs: OpenTab[] = [];

  // Cards abertos agora ou deixados abertos numa página.
  for (const card of listOpenCards()) {
    const openNow = card.leftAt === null;
    tabs.push({
      id: `card:${card.page}`,
      kind: 'card',
      label: card.label,
      sub: pageLabel(card.page),
      path: card.path,
      hasDraft: false,
      isActive: (pathname) => openNow && pathname === card.page,
      close: () => forgetOpenCard(card.page),
      closeDisabled: openNow,
    });
  }

  // Conversas: a atual + as que têm rascunho (WhatsApp e nota do atendimento manual).
  const current = getCurrentConversation();
  const drafts = listDrafts();
  const waDraft = (phone: string) => drafts.find((d) => d.scope === `wa:${phone}`);
  if (current) {
    const draft = waDraft(current.phone);
    tabs.push({
      id: `conv:${current.phone}`,
      kind: 'conversation',
      label: current.name || current.phone,
      path: `${CHAT_PATH}?focusPhone=${encodeURIComponent(current.phone)}`,
      hasDraft: !!draft,
      isActive: (pathname) => pathname === CHAT_PATH,
      close: () => {
        if (draft) discardDraft(draft.scope);
        setCurrentConversation(null);
      },
    });
  }
  for (const { scope, draft } of drafts) {
    const [kind, ref] = scope.split(':');
    if (kind === 'wa') {
      if (current?.phone === ref) continue;
      tabs.push({
        id: `conv:${ref}`,
        kind: 'conversation',
        label: draft.label || ref,
        path: `${CHAT_PATH}?focusPhone=${encodeURIComponent(ref)}`,
        hasDraft: true,
        isActive: () => false,
        close: () => discardDraft(scope),
      });
    } else if (kind === 'manual') {
      tabs.push({
        id: `manual:${ref}`,
        kind: 'conversation',
        label: draft.label || 'Atendimento',
        sub: 'nota',
        path: `${CHAT_PATH}?focusSessionId=${ref}`,
        hasDraft: true,
        isActive: () => false,
        close: () => discardDraft(scope),
      });
    }
  }

  // Formulários pela metade (a página lembra que estava aberto + os campos).
  let keys: string[] = [];
  try { keys = Object.keys(sessionStorage); } catch { /* ignora */ }
  for (const key of keys) {
    const sector = key.match(/^sophia:boardCompleto:(.+):createOpen$/)?.[1];
    if (sector && readFlag(`boardCompleto:${sector}:createOpen`)) {
      tabs.push({
        id: `form:card:${sector}`,
        kind: 'form',
        label: 'Novo card',
        sub: pageLabel(`/board/${sector}`),
        path: `/board/${sector}`,
        hasDraft: true,
        isActive: (pathname) => pathname === `/board/${sector}`,
        close: () => {
          writePersisted(`boardCompleto:${sector}:createOpen`, false);
          clearPersisted(`cardCreate:${sector}:`);
        },
      });
    }
  }
  const forms: { flag: string; id: string; label: string; path: string; draftPrefix: string }[] = [
    { flag: 'matriculas:showNewModal', id: 'form:matricula', label: 'Nova matrícula', path: '/atendimento/matriculas', draftPrefix: 'novaMatricula:' },
    { flag: 'captacao:showNewLead', id: 'form:lead', label: 'Novo lead', path: '/atendimento/captacao', draftPrefix: 'novoLead:' },
    { flag: 'conversas:showNewModal', id: 'form:atendimento', label: 'Novo atendimento', path: CHAT_PATH, draftPrefix: 'atendimentoForm:novo-atendimento:' },
  ];
  for (const form of forms) {
    if (!readFlag(form.flag)) continue;
    tabs.push({
      id: form.id,
      kind: 'form',
      label: form.label,
      path: form.path,
      hasDraft: true,
      isActive: (pathname) => pathname === form.path,
      close: () => {
        writePersisted(form.flag, false);
        clearPersisted(form.draftPrefix);
      },
    });
  }

  return tabs;
}

/** Abas na ordem em que apareceram (estável); quem sumiu sai da ordem. */
export function collectOpenTabs(): OpenTab[] {
  const tabs = collect();
  let order: string[] = [];
  try { order = JSON.parse(sessionStorage.getItem(ORDER_KEY) ?? '[]') as string[]; } catch { order = []; }
  const ids = new Set(tabs.map((t) => t.id));
  const nextOrder = order.filter((id) => ids.has(id));
  for (const t of tabs) if (!nextOrder.includes(t.id)) nextOrder.push(t.id);
  if (nextOrder.join('|') !== order.join('|')) {
    try { sessionStorage.setItem(ORDER_KEY, JSON.stringify(nextOrder)); } catch { /* ignora */ }
  }
  return nextOrder.map((id) => tabs.find((t) => t.id === id)!);
}
